import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

function AcceptInvite() {
  const { token } = useParams()
  const navigate = useNavigate()
  const { signUp } = useAuth()

  const [loading, setLoading] = useState(true)
  const [invite, setInvite] = useState(null)
  const [error, setError] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    validateToken()
  }, [token])

  const validateToken = async () => {
    try {
      setLoading(true)

      // Fetch invite token details
      const { data, error: fetchError } = await supabase
        .from('invite_tokens')
        .select('*, contractors(company_name)')
        .eq('token', token)
        .is('accepted_at', null)
        .single()

      if (fetchError || !data) {
        console.error('Fetch error:', fetchError)
        console.log('Token being validated:', token)
        setError(`Invalid or expired invitation link. ${fetchError ? `Error: ${fetchError.message}` : 'Token not found.'}`)
        setLoading(false)
        return
      }

      // Check if expired
      if (new Date(data.expires_at) < new Date()) {
        setError('This invitation has expired. Please contact your team administrator for a new invitation.')
        setLoading(false)
        return
      }

      setInvite(data)
      setLoading(false)
    } catch (err) {
      console.error('Error validating token:', err)
      setError('Failed to validate invitation')
      setLoading(false)
    }
  }

  const handleAcceptInvite = async (e) => {
    e.preventDefault()
    setError('')
    setCreating(true)

    // Validation
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      setCreating(false)
      return
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters')
      setCreating(false)
      return
    }

    try {
      // Step 1: Create Supabase auth account
      // Use full_name from invitation if available, otherwise use the entered name
      const nameToUse = invite.full_name || fullName

      if (!nameToUse) {
        setError('Full name is required')
        setCreating(false)
        return
      }

      const authData = await signUp(
        invite.email,
        password,
        nameToUse,
        invite.contractors.company_name
      )

      if (!authData || !authData.user) {
        throw new Error('Failed to create account')
      }

      // Step 2: Create user record via Netlify Function (bypasses RLS)
      // Use role and access_mode from the invite token
      const accountResponse = await fetch('/.netlify/functions/create-team-member', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: authData.user.id,
          email: invite.email,
          fullName: nameToUse,
          contractorId: invite.contractor_id,
          inviteTokenId: invite.id,
          role: invite.role || 'technician',
          accessMode: invite.access_mode || 'office'
        })
      })

      const accountData = await accountResponse.json()

      if (!accountResponse.ok || accountData.error) {
        throw new Error(accountData.error || 'Failed to create user profile')
      }

      // Success! User is created, email is auto-confirmed, and invite is marked as accepted
      alert('Account created successfully! You can now sign in with your email and password.')
      navigate('/login')

    } catch (err) {
      console.error('Error accepting invite:', err)
      setError(err.message || 'Failed to accept invitation')
      setCreating(false)
    }
  }

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '60px' }}>
        <div className="card" style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
          <h2 style={{ fontSize: '24px', marginBottom: '15px' }}>Validating Invitation...</h2>
          <p style={{ color: '#7f8c8d' }}>Please wait while we verify your invitation.</p>
        </div>
      </div>
    )
  }

  if (error && !invite) {
    return (
      <div className="container" style={{ paddingTop: '60px' }}>
        <div className="card" style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>❌</div>
          <h2 style={{ fontSize: '24px', marginBottom: '15px', color: '#ef4444' }}>Invalid Invitation</h2>
          <p style={{ color: '#7f8c8d', marginBottom: '25px' }}>{error}</p>
          <Link to="/" className="btn btn-primary">Return to Home</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="container" style={{ paddingTop: '40px' }}>
      <div className="card" style={{ maxWidth: '550px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div style={{ fontSize: '48px', marginBottom: '15px' }}>🎉</div>
          <h2 style={{ fontSize: '24px', marginBottom: '10px' }}>You're Invited!</h2>
          <p style={{ color: '#7f8c8d', fontSize: '16px' }}>
            Join <strong>{invite.contractors.company_name}</strong> on Krinos AI
          </p>
        </div>

        <div style={{
          background: '#f0f9ff',
          border: '1px solid #90caf9',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '25px'
        }}>
          <div style={{ fontSize: '14px', color: '#1976d2', marginBottom: '8px' }}>
            <strong>Invitation Details:</strong>
          </div>
          {invite.full_name && (
            <div style={{ fontSize: '14px', color: '#0c4a6e' }}>
              Name: <strong>{invite.full_name}</strong>
            </div>
          )}
          <div style={{ fontSize: '14px', color: '#0c4a6e' }}>
            Email: <strong>{invite.email}</strong>
          </div>
          <div style={{ fontSize: '14px', color: '#0c4a6e' }}>
            Company: <strong>{invite.contractors.company_name}</strong>
          </div>
        </div>

        {error && (
          <div className="error" style={{ marginBottom: '20px' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleAcceptInvite}>
          {!invite.full_name && (
            <div className="form-group">
              <label htmlFor="fullName">Full Name</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                placeholder="Your full name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="password">Create Password</label>
            <input
              type="password"
              id="password"
              name="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <span className="helper-text">Minimum 6 characters</span>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={creating}
          >
            {creating ? 'Creating Account...' : 'Accept Invitation & Create Account'}
          </button>

          <p style={{ textAlign: 'center', fontSize: '12px', color: '#94a3b8', marginTop: '12px' }}>
            By accepting this invitation, you agree to join {invite.contractors.company_name}'s team on Krinos AI
          </p>
        </form>

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <p style={{ color: '#7f8c8d', fontSize: '14px' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: '#667eea', textDecoration: 'none', fontWeight: '600' }}>
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

export default AcceptInvite
